
import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  useNavigate,
  Navigate,
  useSearchParams,
  useLocation,
} from "react-router-dom";

import LeadFinalApi from "../../api/leadFinalApi";
import Auth from "../../utils/AuthUtils";
import { can } from "../../utils/permissionUtils";
import { showError, showSuccess } from "../../utils/toastUtils";
import { DISPOSITION_COLORS } from "../../utils/dispositionColors";
import { formatDate } from "../../helpers/helper";
import AssignLead from "./AssignLead";
import RowsPerPage from "../../components/partials/RowsPerPage";
import Pagination from "../../components/partials/Pagination";
import SourceApi from "../../api/SourceApi";
import StateApi from "../../api/StateApi";
import UserApi from "../../api/UserApi";
import LeadImportApi from "../../api/LeadImportApi";

function List() {
  const excelInputRef = useRef(null);
  const [importingExcel, setImportingExcel] = useState(false);
  const [importReport, setImportReport] = useState(null);

  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState(
    searchParams.get("search") || ""
  );

  const [dispositionFilter, setDispositionFilter] = useState("");
  const [pagination, setPagination] = useState({});
  const navigate = useNavigate();
  const location = useLocation();
  const user = Auth.getUserDetails();

  const userRole = String(user?.role || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "");

  const isSuperAdmin = userRole === "superadmin";

  const permissions = user?.permissions;
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const hasReadAccess = can(permissions, "read", "lead");

  const [temperatureFilter, setTemperatureFilter] = useState("");
  const [agreementFilter, setAgreementFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [stateFilter, setStateFilter] = useState("");

  const [sources, setSources] = useState([]);
  const [states, setStates] = useState([]);
  const [salesUsers, setSalesUsers] = useState([]);

  const dispositionOptions = [
    ...new Set(leads.map((lead) => lead.disposition).filter(Boolean)),
  ];

  const query = React.useMemo(
    () => ({
      page: Number(searchParams.get("page")) || 1,
      limit: Number(searchParams.get("limit")) || 10,
      search: searchParams.get("search") || "",
      disposition: searchParams.get("disposition") || "",
      leadTemperature: searchParams.get("leadTemperature") || "",
      agreementStatus: searchParams.get("agreementStatus") || "",
      sourceId: searchParams.get("sourceId") || "",
      platform: searchParams.get("platform") || "",
      state: searchParams.get("state") || "",
      assignedTo: searchParams.get("assignedTo") || "",
      from: searchParams.get("from") || "",
      to: searchParams.get("to") || "",
    }),
    [searchParams]
  );

  useEffect(() => {
    setSearchTerm(searchParams.get("search") || "");
  }, [searchParams]);

  const searchTimeout = useRef();

  const handleSearch = (value) => {
    clearTimeout(searchTimeout.current);

    searchTimeout.current = setTimeout(() => {
      updateFilter("search", value);
    }, 400);
  };

  useEffect(() => {
    loadFilters();
  }, []);

  const loadFilters = async () => {
    try {
      const [sourceRes, stateRes, salesUsersRes] = await Promise.all([
        SourceApi.list(),
        StateApi.getStates(),
        UserApi.salesUsers(),
      ]);

      if (salesUsersRes.code === 200) {
        setSalesUsers(salesUsersRes.data || []);
      }

      if (sourceRes.code === 200) {
        setSources(sourceRes.data || []);
      }

      if (stateRes.code === 200) {
        setStates(stateRes.data || []);
      }
    } catch (err) {
      console.error("Failed to load lead filters:", err);
    }
  };

  const updateFilter = useCallback(
    (key, value) => {
      const params = new URLSearchParams(searchParams);

      if (value) {
        params.set(key, value);
      } else {
        params.delete(key);
      }

      if (key !== "page") {
        params.set("page", 1);
      }

      setSearchParams(params);
    },
    [searchParams, setSearchParams]
  );

  const fetchLeads = useCallback(async () => {
    try {
      setLoading(true);

      const res = await LeadFinalApi.list(query);
      const payload = res?.data?.data ?? res?.data ?? res;

      const leadsData = payload?.leads;
      const paginationData = payload?.pagination;

      if (Array.isArray(leadsData)) {
        setLeads(leadsData);
        setPagination(paginationData || {});
      } else {
        console.error("Unexpected lead list response structure:", payload);
        setLeads([]);
        setPagination({});
        showError("Unexpected response format from leads API");
      }
    } catch (err) {
      console.error("Lead fetch error:", err);
      showError(err.message || "Failed to fetch leads");
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => {
    if (hasReadAccess) {
      fetchLeads();
    }
  }, [hasReadAccess, fetchLeads]);

  const handleExcelImport = async (event) => {
    const file = event.target.files?.[0];

    // Same file ko dobara select karne ki permission.
    event.target.value = "";

    if (!file) return;

if (!/\.(xlsx|xls)$/i.test(file.name)) {
  showError("Please select an .xlsx or .xls file.");
  return;
}

    setImportingExcel(true);
    setImportReport(null);

    try {
   const response = await LeadImportApi.importExcel(file);

const payload =
  response?.success !== undefined
    ? response
    : response?.data?.success !== undefined
      ? response.data
      : response?.data ?? response;

      if (!payload?.success) {
        showError(payload?.message || "Excel import failed.");
        return;
      }

      const report = payload.data ?? {};
      setImportReport(report);

    showSuccess(
  `Import completed: ${report.imported ?? 0} new leads created, ${report.updated ?? 0} existing leads updated, and ${report.assigned ?? 0} new leads assigned.`
);

      await fetchLeads();
    } catch (error) {
      showError(
        error?.response?.data?.message ||
          error.message ||
          "Failed to import Excel."
      );
    } finally {
      setImportingExcel(false);
    }
  };

  const handleDelete = async () => {
    if (!can(permissions, "delete", "lead")) {
      showError("Access Denied");
      return;
    }

    try {
      await LeadFinalApi.delete({ id: selectedLead._id });

      const modalEl = document.getElementById("delete-lead-modal");
      window.bootstrap?.Modal?.getInstance(modalEl)?.hide();

      await fetchLeads();
      showSuccess("Lead deleted successfully");
    } catch (error) {
      showError(error?.message || "Delete failed");
    }
  };

  const toggleSelectAll = (event) => {
    setSelectedIds(
      event.target.checked ? leads.map((lead) => lead._id) : []
    );
  };

  const toggleSelectOne = (id) => {
    setSelectedIds((previous) =>
      previous.includes(id)
        ? previous.filter((item) => item !== id)
        : [...previous, id]
    );
  };

  const handleAutoAssign = async () => {
    try {
      const response = await LeadFinalApi.autoAssign();

      if (response.code === 200) {
        showSuccess(response.message);
        setSelectedIds([]);
        await fetchLeads();
      } else {
        showError(response.message || "Auto assign failed");
      }
    } catch (error) {
      showError(error?.message || "Something went wrong");
    }
  };

  if (!hasReadAccess) {
    return <Navigate to="/403" replace />;
  }

  return (
    <div className="content pb-0">
      <div>
        <h4 className="mb-0">
          Lead Management
          <span className="badge badge-soft-primary ms-2">
            {leads.length}
          </span>
        </h4>
      </div>

      <div className="row">
        <div className="col-md-12">
          <div className="countablepagination">
            <p>
              Showing{" "}
              {(Number(pagination?.page || 1) - 1) *
                Number(pagination?.limit || 10) +
                1}{" "}
              to{" "}
              {Math.min(
                Number(pagination?.page || 1) *
                  Number(pagination?.limit || 10),
                Number(pagination?.total || 0)
              )}{" "}
              of {pagination?.total || 0} results
            </p>
          </div>
        </div>
      </div>

      <br />

      <div className="lead-filter-card">
        <div className="lead-filter-body">

          {/* ================= TOP FILTER ROW ================= */}

          {importReport && (
            <div className="alert alert-success mt-3">
              <div className="d-flex justify-content-between align-items-start">
                <h6 className="mb-2">Excel Import Report</h6>

                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  onClick={() => setImportReport(null)}
                />
              </div>

              <div>
  Total rows:{" "}
  <strong>{importReport.totalRows ?? 0}</strong>
  {" | "}
  Imported:{" "}
  <strong>{importReport.imported ?? 0}</strong>
  {" | "}
  Updated:{" "}
  <strong>{importReport.updated ?? 0}</strong>
  {" | "}
  Assigned:{" "}
  <strong>{importReport.assigned ?? 0}</strong>
  {" | "}
  Skipped:{" "}
  <strong>{importReport.skipped ?? 0}</strong>
</div>

              {importReport.assignments?.length > 0 && (
                <div className="mt-3">
                  <strong>Sales-wise distribution</strong>

                  <ul className="mb-0 mt-2">
                    {importReport.assignments.map((item) => (
                      <li key={item.userId}>
                        {item.name || "Sales User"}: {item.count} leads
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {importReport.errors?.length > 0 && (
                <details className="mt-3">
                  <summary>
                    View skipped/error rows ({importReport.errors.length})
                  </summary>

                  <ul className="mt-2 mb-0">
                    {importReport.errors.slice(0, 20).map((item, index) => (
                      <li key={`${item.row}-${index}`}>
                        Row {item.row}: {item.reason}
                      </li>
                    ))}
                  </ul>

                  {importReport.errors.length > 20 && (
                    <small>
                      Showing first 20 errors. Total errors:{" "}
                      {importReport.errors.length}
                    </small>
                  )}
                </details>
              )}
            </div>
          )}

          <div className="lead-filter-top">
            {/* Search */}
            <div className="lead-search-wrap">
              <div className="lead-search-box">
                <i className="ti ti-search"></i>

                <input
                  type="text"
                  className="lead-search-input"
                  placeholder="Search by Name / Phone..."
                  value={searchTerm}
                  onChange={(event) => {
                    setSearchTerm(event.target.value);
                    handleSearch(event.target.value);
                  }}
                />
              </div>
            </div>
                  
            {/* Disposition */}
            <div className="lead-disposition-wrap">
              <select
                className="form-select lead-filter-select"
                value={query.disposition}
                onChange={(event) =>
                  updateFilter("disposition", event.target.value)
                }
              >
                <option value="">All Disposition</option>

                {dispositionOptions.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </div>

            {/* Actions */}
            <div className="lead-filter-actions">
              {selectedIds.length > 0 &&
                can(permissions, "write", "lead") && (
                  <button
                    type="button"
                    className="btn lead-assign-btn"
                    data-bs-toggle="modal"
                    data-bs-target="#standard-modal"
                  >
                    <i className="ti ti-user-check"></i>
                    Assign ({selectedIds.length})
                  </button>
                )}

              {/* Excel import: Super Admin only */}
              {isSuperAdmin && (
                <>
                  <input
                    ref={excelInputRef}
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleExcelImport}
                    style={{ display: "none" }}
                  />

                  <button
                    type="button"
                    className="btn btn-success"
                    disabled={importingExcel}
                    onClick={() => excelInputRef.current?.click()}
                  >
                    <i className="ti ti-file-spreadsheet me-1"></i>
                    {importingExcel ? "Importing..." : "Import Excel"}
                  </button>
                </>
              )}

              {can(permissions, "write", "lead") && (
                <button
                  type="button"
                  className="btn lead-add-btn"
                  onClick={() => window.open("/LeadRegistration", "_blank")}
                >
                  <i className="ti ti-plus"></i>
                  Add Lead
                </button>
              )}
            </div>
          </div>

          {/* ================= SECOND FILTER ROW ================= */}

          <div className="lead-filter-grid">
            {/* Agreement */}
            <div className="lead-filter-item">
              <label>Agreement Status</label>

              <select
                className="form-select lead-filter-select"
                value={query.agreementStatus}
                onChange={(event) =>
                  updateFilter("agreementStatus", event.target.value)
                }
              >
                <option value="">All Agreement Status</option>
                <option value="not_sent">📄 Not Sent</option>
                <option value="pending">⏳ Pending</option>
                <option value="agreed">✅ Agreed</option>
                <option value="not_agreed">❌ Rejected</option>
              </select>
            </div>

            {/* Lead Owner */}
            <div className="lead-filter-item">
              <label>Lead Owner</label>

              <select
                className="form-select lead-filter-select"
                value={query.assignedTo}
                onChange={(event) =>
                  updateFilter("assignedTo", event.target.value)
                }
              >
                <option value="">All Lead Owners</option>

                {salesUsers.map((salesUser) => (
                  <option key={salesUser._id} value={salesUser._id}>
                    {salesUser.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Platform */}
            <div className="lead-filter-item">
              <label>Platform</label>

              <select
                className="form-select lead-filter-select"
                value={query.platform}
                onChange={(event) =>
                  updateFilter("platform", event.target.value)
                }
              >
                <option value="">All Platform</option>
                <option value="axesell.in">Axesell.in</option>
                <option value="axesell.com">Axesell.com</option>
                <option value="ig">Instagram</option>
                <option value="fb">Facebook</option>
                <option value="Unknown">Unknown</option>
              </select>
            </div>

            {/* Temperature */}
            <div className="lead-filter-item">
              <label>Lead Temperature</label>

              <select
                className="form-select lead-filter-select"
                value={query.leadTemperature}
                onChange={(event) =>
                  updateFilter("leadTemperature", event.target.value)
                }
              >
                <option value="">All Temperature</option>
                <option value="Hot">🔥 Hot</option>
                <option value="Warm">🟡 Warm</option>
                <option value="Cold">🔵 Cold</option>
              </select>
            </div>

            {/* From Date */}
            <div className="lead-filter-item">
              <label>From Date</label>

              <div className="lead-date-box">
                <input
                  type="date"
                  className="form-control lead-date-input"
                  value={query.from}
                  onChange={(event) => updateFilter("from", event.target.value)}
                />
                <i className="ti ti-calendar"></i>
              </div>
            </div>

            {/* To Date */}
            <div className="lead-filter-item">
              <label>To Date</label>

              <div className="lead-date-box">
                <input
                  type="date"
                  className="form-control lead-date-input"
                  value={query.to}
                  onChange={(event) => updateFilter("to", event.target.value)}
                />
                <i className="ti ti-calendar"></i>
              </div>
            </div>

            {/* Clear Filters */}
            <div className="lead-filter-clear-wrap">
              <button
                type="button"
                className="lead-clear-btn"
                onClick={() => {
                  setSearchTerm("");
                  setSelectedIds([]);
                  setSearchParams({});
                }}
              >
                <i className="ti ti-refresh"></i>
                <span>Clear Filters</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Lead table continues in Part 3 */}

      <div className="card border-0 rounded-0 shadow-sm">
        <div className="card-body">
          <div className="table-responsive custom-table">
            <table className="table table-hover">
              <thead className="thead-light">
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      className="form-check-input"
                      onChange={toggleSelectAll}
                      checked={
                        leads.length > 0 &&
                        selectedIds.length === leads.length
                      }
                    />
                  </th>
                  <th>S.No</th>
                  <th>Company &amp; Name</th>
                  <th>Source</th>
                  <th>Contact Details</th>
                  <th>Lead Type</th>
                  <th>Created Date</th>
                  <th>Assigned To</th>
                  {isSuperAdmin && <th>Transferred From</th>}
                  <th>Disposition</th>
                  <th>Proposal Status</th>
                  <th>Agreement Status</th>
                  <th>Latest Followups</th>
                  <th className="text-center sticky-action">Action</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={isSuperAdmin ? 14 : 13} className="text-center">
                      Loading Leads...
                    </td>
                  </tr>
                ) : leads.length > 0 ? (
                  leads.map((item, index) => {
                    const colorClass =
                      DISPOSITION_COLORS[item.disposition] || "bg-secondary";

                    return (
                      <tr key={item._id}>
                        <td>
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={selectedIds.includes(item._id)}
                            onChange={() => toggleSelectOne(item._id)}
                          />
                        </td>

                        <td>
                          {index +
                            (Number(pagination.page || 1) - 1) *
                              Number(pagination.limit || 10) +
                            1}
                        </td>

                        <td>
                          <div
                            onClick={() =>
                              navigate(
                                `/lead-details/${item._id}${location.search}`
                              )
                            }
                            className="lead-name-link"
                          >
                            <span className="text-danger small">
                              {item.company_name || ""}
                            </span>
                            <br />
                            <strong>{item.name}</strong>
                          </div>
                        </td>

                        <td>
                          <span
                            className={`badge ${
                              item?.meta?.platform === "axesell.in"
                                ? "bg-primary"
                                : item?.meta?.platform === "axesell.com"
                                  ? "bg-success"
                                  : "bg-secondary"
                            }`}
                          >
                            {item?.meta?.platform || "Unknown"}
                          </span>
                        </td>

                        <td>
                          <span className="small">{item.phone}</span>
                          <br />
                          <span className="text-muted small">
                            {item.email || ""}
                          </span>
                        </td>

                        <td>
                          {item.leadTemperature === "Hot" && (
                            <span className="badge bg-danger">🔥 Hot</span>
                          )}

                          {item.leadTemperature === "Warm" && (
                            <span className="badge bg-warning text-dark">
                              🟡 Warm
                            </span>
                          )}

                          {item.leadTemperature === "Cold" && (
                            <span className="badge bg-primary">🔵 Cold</span>
                          )}
                        </td>

                        <td>{formatDate(item.createdAt, true)}</td>

                        <td>
                          <div className="fw-semibold">
                            {item.assignedTo?.name || "-"}
                          </div>
                        </td>

                        {isSuperAdmin && (
                          <td>
                            {item.reassignedFrom ? (
                              <span
                                className="badge bg-warning text-dark"
                                title={`This lead was transferred from ${item.reassignedFrom.name}`}
                              >
                                <i className="ti ti-transfer me-1"></i>
                                {item.reassignedFrom.name}
                              </span>
                            ) : (
                              <span className="text-muted">-</span>
                            )}
                          </td>
                        )}

                        <td>
                          <span className={`badge badge-pill ${colorClass}`}>
                            {item.disposition || "-"}
                          </span>
                        </td>

                        <td>
                          {item.documentStatus === "accepted" && (
                            <span className="badge bg-success">
                              ✅ Accepted
                            </span>
                          )}

                          {item.documentStatus === "contact_sales" && (
                            <div>
                              <span className="badge bg-warning text-dark">
                                📞 Contact Required
                              </span>
                              <div className="small text-warning">
                                Client needs discussion
                              </div>
                            </div>
                          )}

                          {(!item.documentStatus ||
                            item.documentStatus === "pending") && (
                            <span className="badge bg-secondary">
                              ⏳ Pending
                            </span>
                          )}
                        </td>

                        <td>
                          {item.agreementStatus === "agreed" && (
                            <div
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "6px",
                                background: "#e8f7ee",
                                color: "#119c4b",
                                padding: "7px 14px",
                                borderRadius: "999px",
                                fontWeight: "600",
                                fontSize: "13px",
                              }}
                            >
                              ✅ Agreed
                            </div>
                          )}

                          {item.agreementStatus === "not_agreed" && (
                            <div
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "6px",
                                background: "#ffe9e9",
                                color: "#d92d20",
                                padding: "7px 14px",
                                borderRadius: "999px",
                                fontWeight: "600",
                                fontSize: "13px",
                              }}
                            >
                              ❌ Rejected
                            </div>
                          )}

                          {item.agreementStatus === "pending" && (
                            <div
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "6px",
                                background: "#fff4df",
                                color: "#d97706",
                                padding: "7px 14px",
                                borderRadius: "999px",
                                fontWeight: "600",
                                fontSize: "13px",
                              }}
                            >
                              ⏳ Pending
                            </div>
                          )}

                          {(!item.agreementStatus ||
                            item.agreementStatus === "not_sent") && (
                            <div
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "6px",
                                background: "#f1f3f5",
                                color: "#555",
                                padding: "7px 14px",
                                borderRadius: "999px",
                                fontWeight: "600",
                                fontSize: "13px",
                              }}
                            >
                              📄 Not Sent
                            </div>
                          )}
                        </td>

                        <td style={{ width: "300px" }}>
                          {item.latestFollowups?.length > 0 ? (
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "6px",
                              }}
                            >
                              {item.latestFollowups
                                .slice(0, 2)
                                .map((followup, followupIndex) => (
                                  <div
                                    key={followupIndex}
                                    title={followup.remarks}
                                    style={{
                                      background: "#f8f9fc",
                                      border: "1px solid #ececec",
                                      borderRadius: "8px",
                                      padding: "8px 12px",
                                      fontSize: "13px",
                                      color: "#555",
                                      overflow: "hidden",
                                      whiteSpace: "nowrap",
                                      textOverflow: "ellipsis",
                                      height: "38px",
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    <i className="ti ti-message-circle me-2 text-primary"></i>
                                    <span>
                                      {followup.remarks || "No Remarks"}
                                    </span>
                                  </div>
                                ))}
                            </div>
                          ) : (
                            <span className="text-muted small">
                              No Followups
                            </span>
                          )}
                        </td>

                        <td className="text-center sticky-action">
                          <div className="d-flex justify-content-center gap-2">
                            {can(permissions, "read", "lead") && (
                              <button
                                type="button"
                                className="btn btn-soft-success btn-icon btn-sm"
                                onClick={() =>
                                  navigate(
                                    `/lead-details/${item._id}${location.search}`
                                  )
                                }
                                title="View Details"
                              >
                                <i className="ti ti-eye"></i>
                              </button>
                            )}

                            {can(permissions, "delete", "lead") && (
                              <button
                                type="button"
                                className="btn btn-soft-danger btn-icon btn-sm"
                                data-bs-toggle="modal"
                                data-bs-target="#delete-lead-modal"
                                onClick={() => setSelectedLead(item)}
                                title="Delete"
                              >
                                <i className="ti ti-trash"></i>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td
                      colSpan={isSuperAdmin ? 14 : 13}
                      className="text-center py-5 text-muted"
                    >
                      <i className="ti ti-search fs-2 mb-2 d-block"></i>
                      {dispositionFilter
                        ? `No leads found for "${dispositionFilter}" status`
                        : searchTerm
                          ? "No leads match your search"
                          : "No leads available"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {pagination.page && (
            <div className="row">
              <RowsPerPage
                value={searchParams.get("limit") || "10"}
                onChange={(value) => updateFilter("limit", value)}
              />

              <Pagination
                pagination={pagination}
                updateFilter={updateFilter}
              />
            </div>
          )}
        </div>
      </div>

      {/* Modals and component closing tags continue in Part 4 */}

      {/* ================= ASSIGN LEAD MODAL ================= */}

      <div id="standard-modal" className="modal fade" tabIndex={-1}>
        <AssignLead
          data={selectedIds}
          onAssigned={() => {
            fetchLeads();
            setSelectedIds([]);
          }}
        />
      </div>

      {/* ================= DELETE LEAD MODAL ================= */}

      <div
        className="modal fade"
        id="delete-lead-modal"
        tabIndex={-1}
        aria-labelledby="deleteLeadModalTitle"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered modal-sm">
          <div className="modal-content">
            <div className="modal-body text-center p-4">
              <span className="avatar avatar-xl badge-soft-danger rounded-circle mb-3">
                <i className="ti ti-trash fs-24"></i>
              </span>

              <h5 className="mb-1" id="deleteLeadModalTitle">
                Confirm Delete
              </h5>

              <p>Are you sure you want to delete this lead?</p>

              <div className="d-flex gap-2 mt-3">
                <button
                  type="button"
                  className="btn btn-light w-100"
                  data-bs-dismiss="modal"
                >
                  Cancel
                </button>

                <button
                  type="button"
                  className="btn btn-danger w-100"
                  onClick={handleDelete}
                >
                  Yes, Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default List;
